import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Building2, MapPin, Users, BookOpen, DollarSign, Plus, Edit2, MoreVertical, Crown } from 'lucide-react';
import api from '../lib/api';

export default function Locations() {
  const { tenantId } = useParams();
  const [locations, setLocations] = useState([]);
  const [planInfo, setPlanInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address_line1: '',
    city: '',
    state: '',
    postal_code: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    fetchLocations();
    fetchPlanInfo();
  }, [tenantId]);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations');
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanInfo = async () => {
    try {
      // Get tenant info to show plan limits
      const response = await api.get('/users/me');
      setPlanInfo(response.data.tenant);
    } catch (error) {
      console.error('Failed to fetch plan info:', error);
    }
  };

  // Get location limit based on plan (matches API in /api/lib/plans.js)
  const getLocationLimit = (plan) => {
    const planName = plan?.toLowerCase() || 'starter';
    // Starter and Growth: 1 location
    // Professional (and legacy complete/business): 5 locations
    // Enterprise: unlimited (null)
    if (['starter', 'essentials', 'growth'].includes(planName)) {
      return 1;
    }
    if (['professional', 'complete', 'business'].includes(planName)) {
      return 5;
    }
    return null; // Enterprise - unlimited
  };

  // Get display text for location limit
  const getLocationLimitDisplay = (plan) => {
    const limit = getLocationLimit(plan);
    return limit === null ? '∞' : limit;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await api.put(`/locations/${editingLocation.id}`, formData);
      } else {
        await api.post('/locations', formData);
      }
      setShowAddModal(false);
      setEditingLocation(null);
      setFormData({
        name: '',
        code: '',
        address_line1: '',
        city: '',
        state: '',
        postal_code: '',
        phone: '',
        email: ''
      });
      fetchLocations();
    } catch (error) {
      console.error('Failed to save location:', error);
      const errorData = error.response?.data;
      if (errorData?.upgradeRequired) {
        alert(`${errorData.message}\n\nCurrent: ${errorData.currentCount} of ${errorData.currentLimit} location(s)`);
      } else {
        alert(errorData?.error || errorData?.message || 'Failed to save location');
      }
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      code: location.code,
      address_line1: location.address_line1 || '',
      city: location.city || '',
      state: location.state || '',
      postal_code: location.postal_code || '',
      phone: location.phone || '',
      email: location.email || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm('Are you sure you want to deactivate this location?')) return;
    try {
      await api.delete(`/locations/${locationId}`);
      fetchLocations();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete location');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Locations</h1>
          <p className="text-gray-600 mt-1">
            Manage your campuses, branches, or teaching sites
          </p>
          
          {/* Location Limit Indicator */}
          {planInfo && (
            <div className="mt-3 flex items-center gap-2">
              <span className={`text-sm px-3 py-1 rounded-full ${
                locations.length >= getLocationLimit(planInfo.plan)
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-green-100 text-green-800'
              }`}>
                {locations.length} of {getLocationLimitDisplay(planInfo.plan)} locations used
              </span>
              {['starter', 'growth', 'essentials'].includes(planInfo.plan?.toLowerCase()) && locations.length >= 1 && (
                <a 
                  href="/settings/billing" 
                  className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                >
                  <Crown className="w-4 h-4" />
                  Upgrade to add more
                </a>
              )}
            </div>
          )}
        </div>
        <button
          onClick={() => {
            setEditingLocation(null);
            setFormData({
              name: '',
              code: '',
              address_line1: '',
              city: '',
              state: '',
              postal_code: '',
              phone: '',
              email: ''
            });
            setShowAddModal(true);
          }}
          className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Location
        </button>
      </div>

      {/* Locations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {locations.map((location) => (
          <div
            key={location.id}
            className={`bg-white rounded-lg shadow-sm border p-6 ${
              location.is_primary ? 'border-purple-200 ring-1 ring-purple-100' : 'border-gray-200'
            }`}
          >
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Building2 className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{location.name}</h3>
                  {location.code && (
                    <span className="text-sm text-gray-500">{location.code}</span>
                  )}
                  {location.is_primary && (
                    <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                      Primary
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleEdit(location)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                {!location.is_primary && (
                  <button
                    onClick={() => handleDelete(location.id)}
                    className="p-1 text-gray-400 hover:text-red-600 rounded"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Address */}
            <div className="flex items-start gap-2 text-sm text-gray-600 mb-4">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                {location.address_line1 && <div>{location.address_line1}</div>}
                {(location.city || location.state) && (
                  <div>
                    {location.city}{location.city && location.state && ', '}{location.state} {location.postal_code}
                  </div>
                )}
              </div>
            </div>

            {/* Contact */}
            {(location.phone || location.email) && (
              <div className="text-sm text-gray-600 mb-4 space-y-1">
                {location.phone && <div>{location.phone}</div>}
                {location.email && <div className="text-purple-600">{location.email}</div>}
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-gray-500 text-xs mb-1">
                  <Users className="w-3 h-3" />
                  Students
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {location.stats?.total_students || 0}
                </div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-gray-500 text-xs mb-1">
                  <BookOpen className="w-3 h-3" />
                  Classes
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {location.stats?.total_classes || 0}
                </div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-gray-500 text-xs mb-1">
                  <DollarSign className="w-3 h-3" />
                  Revenue
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  ${(location.stats?.monthly_revenue || 0).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {locations.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No locations yet</h3>
          <p className="text-gray-500 mb-4">Add your first campus or teaching location</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            Add Location
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingLocation ? 'Edit Location' : 'Add Location'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Main Campus"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., MAIN"
                />
                <p className="text-xs text-gray-500 mt-1">Short code for reports</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={formData.address_line1}
                  onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Street address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {editingLocation ? 'Save Changes' : 'Add Location'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
